import type { CorpsPush, ResultatMutation } from '../types.js';
import type { Journal } from './journal.js';

/**
 * Rejeu de la file d'envoi (L22, expert 3 §5.3). La file est envoyée par lots, dans l'ordre de saisie. Chaque mutation
 * acquittée « applique » sort de la file ; « conflit » et « rejete » en sortent aussi mais restent présentées à
 * l'utilisateur (jamais de perte silencieuse) ; une mutation sans accusé reste en file. Une coupure interrompt le rejeu
 * et conserve tout ce qui n'est pas acquitté. Un seul rejeu à la fois par journal.
 */
export type TransportPush = (corps: CorpsPush) => Promise<{ results: readonly ResultatMutation[] }>;

export interface OptionsRejeu {
  readonly journal: Journal;
  readonly transport: TransportPush;
  readonly deviceId: string;
  readonly tailleLot?: number;
}

export interface BilanRejeu {
  readonly etat: 'termine' | 'hors_ligne' | 'deja_en_cours';
  readonly envoyees: number;
  readonly conflits: number;
  readonly restantes: number;
}

export const TAILLE_LOT_PAR_DEFAUT = 50;
const DELAI_INITIAL_MS = 2_000;
const DELAI_MAX_MS = 300_000;

const enCours = new WeakSet<Journal>();

/** Minuterie de reprise : 2 s, 4 s, 8 s ... plafonnée à 5 minutes. */
export function delaiReprise(tentative: number): number {
  const rang = Math.max(1, Math.floor(tentative));
  return Math.min(DELAI_INITIAL_MS * 2 ** (rang - 1), DELAI_MAX_MS);
}

export async function rejouer({ journal, transport, deviceId, tailleLot = TAILLE_LOT_PAR_DEFAUT }: OptionsRejeu): Promise<BilanRejeu> {
  if (enCours.has(journal)) return { etat: 'deja_en_cours', envoyees: 0, conflits: 0, restantes: await journal.compter() };
  enCours.add(journal);
  try {
    const file = await journal.enAttente();
    let envoyees = 0;
    let conflits = 0;
    for (let debut = 0; debut < file.length; debut += tailleLot) {
      const lot = file.slice(debut, debut + tailleLot);
      let reponse: { results: readonly ResultatMutation[] };
      try {
        reponse = await transport({ deviceId, mutations: lot });
      } catch {
        return { etat: 'hors_ligne', envoyees, conflits, restantes: await journal.compter() };
      }
      const acquittees: string[] = [];
      for (const r of reponse.results) {
        if (!lot.some((m) => m.mutationId === r.mutationId)) continue;
        if (r.result === 'applique') {
          acquittees.push(r.mutationId);
          envoyees += 1;
        } else {
          await journal.signalerConflit(r.mutationId, r.conflict ?? {});
          conflits += 1;
        }
      }
      await journal.retirer(acquittees);
    }
    return { etat: 'termine', envoyees, conflits, restantes: await journal.compter() };
  } finally {
    enCours.delete(journal);
  }
}
